import { z } from "zod";
import type { PassportField, PassportSchemaDef } from "./types.js";

/**
 * "draft" lets users save partial data while collecting it (supplier data
 * arrives piecemeal); "publish" enforces every `required` field so only
 * compliant passports go live.
 */
export type SchemaMode = "draft" | "publish";

// Calendar date only (no time component): passports carry declaration dates,
// not timestamps, and YYYY-MM-DD survives locale round-trips.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True only for absolute http(s) URLs. Passport url values are rendered as
 * links on the public page, so a javascript:/data: scheme would become a live
 * XSS vector — z.string().url() alone accepts ANY scheme. Exported so the
 * HTML renderer applies the same rule as defense-in-depth.
 */
export function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * True only for real calendar dates. Date.parse would silently roll an
 * impossible date over ("2026-02-30" → March 2nd), so build the Date from its
 * parts and require them to round-trip unchanged.
 */
function isCalendarDate(value: string): boolean {
  const [y = 0, m = 0, d = 0] = value.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

function scalarTypeFor(
  type: Exclude<PassportField["type"], "group">,
  enumValues: readonly string[] | undefined,
  id: string,
): z.ZodTypeAny {
  switch (type) {
    // "string" and "country" validate identically: a non-empty string. "country"
    // additionally holds an ISO 3166-1 alpha-2 code, but the picker constrains
    // input on the way in while pre-existing free-text values must still
    // round-trip — so no stricter check here. Empty strings count as "not
    // provided" and are rejected so publish mode can't be satisfied by blanks.
    case "string":
    case "country":
      return z.string().trim().min(1);
    case "number":
      return z.number().finite();
    case "boolean":
      return z.boolean();
    case "percentage":
      return z.number().min(0).max(100);
    case "url":
      return z.string().url().refine(isHttpUrl, "only http(s) URLs are allowed");
    case "date":
      return z
        .string()
        .regex(ISO_DATE, "expected ISO date (YYYY-MM-DD)")
        .refine(isCalendarDate, "invalid calendar date");
    case "enum": {
      const values = enumValues ?? [];
      const [first, ...rest] = values;
      // A valueless or duplicated enum is a schema-authoring bug — fail loudly
      // at build time, not silently at validation time.
      if (first === undefined) throw new Error(`enum field "${id}" has no enumValues`);
      if (new Set(values).size !== values.length) {
        throw new Error(`enum field "${id}" has duplicate enumValues`);
      }
      return z.enum([first, ...rest]);
    }
  }
}

function baseTypeFor(field: PassportField, mode: SchemaMode): z.ZodTypeAny {
  if (field.type !== "group") return scalarTypeFor(field.type, field.enumValues, field.id);

  const items = field.itemFields ?? [];
  // Same fail-loudly rule as valueless enums: a group without a row shape is a
  // schema-authoring bug.
  if (items.length === 0) throw new Error(`group field "${field.id}" has no itemFields`);
  if (new Set(items.map((i) => i.key)).size !== items.length) {
    throw new Error(`group field "${field.id}" has duplicate item keys`);
  }
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const item of items) {
    const base = scalarTypeFor(item.type, item.enumValues, `${field.id}.${item.key}`);
    // Item-level `required` binds per ROW: any row you do submit must carry its
    // required keys in publish mode; draft rows may stay partial.
    shape[item.key] = mode === "publish" && item.required ? base : base.optional();
  }
  const rows = z.array(z.object(shape));
  // A required group needs at least one row to publish (mirrors min(1) strings).
  return mode === "publish" && field.required ? rows.min(1) : rows;
}

/**
 * Builds the runtime validator for a schema definition. Keys are the flat
 * field dot-path ids (matching PassportAttributes); unknown keys are stripped
 * so stale attrs from a previous schema revision don't fail validation.
 */
export function zodSchemaFor(
  def: PassportSchemaDef,
  opts: { mode: SchemaMode },
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const category of def.categories) {
    for (const field of category.fields) {
      const base = baseTypeFor(field, opts.mode);
      const enforce = opts.mode === "publish" && field.required;
      shape[field.id] = enforce ? base : base.optional();
    }
  }
  return z.object(shape);
}

/** Flat id → field lookup across categories (dot-path ids are globally unique per schema). */
export function fieldsById(def: PassportSchemaDef): Map<string, PassportField> {
  const map = new Map<string, PassportField>();
  for (const category of def.categories) {
    for (const field of category.fields) map.set(field.id, field);
  }
  return map;
}

/**
 * Validator for a SUBSET of a schema's fields — drives the supplier data
 * collection flow, where an invite requests only some field ids. Ids that no
 * longer exist in the schema are skipped (an invite may outlive a template
 * revision; a stale id must not brick the whole form). Draft semantics apply
 * regardless of `required`: a supplier fills what they know, so every field is
 * optional in draft mode and only type-checked when provided.
 */
export function zodSchemaForFields(
  def: PassportSchemaDef,
  fieldIds: readonly string[],
  opts: { mode: SchemaMode },
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const byId = fieldsById(def);
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const id of fieldIds) {
    const field = byId.get(id);
    if (!field) continue;
    const base = baseTypeFor(field, opts.mode);
    const enforce = opts.mode === "publish" && field.required;
    shape[field.id] = enforce ? base : base.optional();
  }
  return z.object(shape);
}
